import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@mygmao/shared';
import { KeyRound, Loader2, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import { api, extractErrorMessage } from '../lib/api';

/**
 * Page /forgot-password (US-AUTH-03).
 *
 * Anti-énumération : la réponse backend est TOUJOURS identique, qu'un
 * compte existe ou non. Le user voit un message générique. Le mdp temporaire
 * est envoyé par email (US-ADM-06) — en dev il est affiché en console.
 */
export function ForgotPasswordPage() {
  const [status, setStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [devToken, setDevToken] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  async function onSubmit(values: ForgotPasswordInput) {
    setErrorMessage(null);
    try {
      const res = await api.post<{
        data: { message: string; dev_token?: string };
      }>('/auth/forgot-password', values);
      setStatus('sent');
      if (res.data.data.dev_token) {
        setDevToken(res.data.data.dev_token);
        // En dev, affiche aussi dans la console pour copier-coller facile.
        console.warn('[DEV] Reset token:', res.data.data.dev_token);
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage(extractErrorMessage(err, 'Demande impossible'));
    }
  }

  if (status === 'sent') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-md w-full p-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Email envoyé</h1>
          <p className="text-sm text-gray-600 mb-4">
            Si un compte existe avec cet email, un lien de réinitialisation vient d'être envoyé.
            Vérifie ta boîte de réception (et les spams).
          </p>
          {devToken && (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-left">
              <p className="text-xs font-semibold text-yellow-800 mb-1">
                🔧 DEV — Token (Resend pas encore branché, cf. US-ADM-06)
              </p>
              <Link
                to={`/reset-password?token=${encodeURIComponent(devToken)}`}
                className="block text-xs text-primary-700 hover:underline break-all"
              >
                /reset-password?token={devToken}
              </Link>
            </div>
          )}
          <Link
            to="/login"
            className="inline-flex items-center mt-6 text-sm text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour à la connexion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-md w-full p-8"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Mot de passe oublié</h1>
            <p className="text-xs text-gray-500">Reçois un lien de réinitialisation par email</p>
          </div>
        </div>

        {status === 'error' && errorMessage && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            {...register('email')}
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Envoi en cours…
            </>
          ) : (
            'Envoyer le lien'
          )}
        </button>

        <div className="mt-4 text-center">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour à la connexion
          </Link>
        </div>
      </form>
    </div>
  );
}
